/*
 * The agent's quiet actions and its rescue card (LEA-225, decision 5: "Distill hard"). More actions
 * is gone: Done closes the draft, so nothing waits behind a fold. Mark booked, while the lead has not
 * agreed, and Draft again sit as quiet buttons on the agent's line, beside "Draft ready". The
 * "Looks wrong or out of date?" card comes out by itself only when the app sees something wrong: a
 * draft that answers an older message, one the checks withheld, or an offer that says a question or
 * a requested draft is stuck. Draft again on the line brings it out too, for a draft the rep doubts.
 */
import { type ReactNode, useState } from "react";
import type { PlanStep, WorkPlan } from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { isDeveloperReviewStep } from "../../src/inbox-view.ts";
import { DraftAgain, useDraftAgainOffer } from "../draft-again.tsx";
import { BookedIcon } from "../icons.tsx";
import { whenLabel } from "./conversation-shared.ts";
import { showsDraftLine } from "./inbox-draft-line.tsx";

/** A withheld draft: the run's checks refused every attempt, so the rep has no draft to send. */
const withheld = (step: PlanStep): boolean =>
  isDeveloperReviewStep(step) && ["pending", "invalidated"].includes(step.state);

export type Rescue = {
  /** The server offers Check and Draft again for this conversation. */
  readonly offered: boolean;
  /** Something is wrong, so the card shows without being asked. */
  readonly needed: boolean;
  /** The rep asked for it from the line. */
  readonly open: boolean;
  readonly show: () => void;
};

function useRescue(plan: WorkPlan, staleDraft: boolean): Rescue {
  const offer = useDraftAgainOffer(plan);
  const [open, setOpen] = useState(false);
  return {
    offered: offer.offered,
    needed: offer.needsYou || staleDraft || plan.steps.some(withheld),
    open,
    show: () => setOpen(true),
  };
}

/** The card, where it is needed or asked for; otherwise only what it has to say after a click. */
export function RescueCard(props: {
  rescue: Rescue;
  plan: WorkPlan;
  busy: boolean;
  editing: boolean;
  onChange: () => Promise<void>;
}) {
  return (
    <DraftAgain
      plan={props.plan}
      disabled={props.busy}
      editing={props.editing}
      folded={!props.rescue.open && !props.rescue.needed}
      when={whenLabel}
      onChange={props.onChange}
    />
  );
}

export type QuietProps = {
  plan: WorkPlan;
  rescue: Rescue;
  /** Mark booked waits on the line until the lead agrees (`completionControls`). */
  markBooked: boolean;
  markBookedLabel: string;
  onBooked: () => void;
  checkHubSpot: ReactNode;
  busy: boolean;
  editing: boolean;
  /** What the item adds to the line, such as Show activity (`agent-item.tsx`). */
  extra?: ReactNode;
};

/** Mark booked, only while nothing has completed or closed the conversation's goal. */
const booking = (props: QuietProps): boolean =>
  props.markBooked && !props.plan.goalCompletion && !props.plan.operatorOutcome;

/** Draft again on the line, while the card is neither needed nor already out. */
const asking = ({ rescue }: QuietProps): boolean =>
  rescue.offered && !rescue.needed && !rescue.open;

/** Whether the line has anything quiet to offer; a closed draft's line carries its own. */
function hasQuiet(props: QuietProps): boolean {
  if (props.plan.operatorOutcome) return false;
  return booking(props) || asking(props) || props.extra !== undefined;
}

/** The quiet buttons themselves, at the end of the agent's line. */
function QuietActions(props: QuietProps) {
  if (!hasQuiet(props)) return null;
  return (
    <span className="lcr-agent-quiet">
      {booking(props) && (
        <button
          type="button"
          className="lcr-small-button lcr-ghost"
          disabled={props.busy || props.editing}
          onClick={props.onBooked}
        >
          <BookedIcon size={16} />
          {props.markBookedLabel}
        </button>
      )}
      {booking(props) && props.checkHubSpot}
      {asking(props) && (
        <button type="button" className="lcr-small-button lcr-ghost" onClick={props.rescue.show}>
          Draft again
        </button>
      )}
      {props.extra}
    </span>
  );
}

/** A reply step still waiting on the rep. */
const openReply = (step: PlanStep): boolean =>
  step.input.kind === "reply" && ["pending", "invalidated"].includes(step.state);

/**
 * The agent's line for the steps on screen: the one healthy draft line ends with the quiet actions;
 * without one they take a line of their own. A stale draft, one that answers an older message,
 * raises the rescue card.
 */
export function useAgentLine(
  input: Omit<QuietProps, "rescue"> & {
    steps: readonly PlanStep[];
    args: (step: PlanStep) => Parameters<typeof showsDraftLine>[0];
    /** Cards that carry their own Mark booked or take the conversation elsewhere; null when absent. */
    cards: readonly (PlanStep | null)[];
  },
) {
  const { steps, args, cards, ...given } = input;
  const rest = { ...given, markBooked: given.markBooked && cards.every((card) => card === null) };
  const rescue = useRescue(
    rest.plan,
    steps.some((step) => openReply(step) && args(step).stale),
  );
  const quiet: QuietProps = { ...rest, rescue };
  const lineStepId = steps.find((step) => showsDraftLine(args(step)))?.id ?? null;
  return {
    rescue,
    actionsFor: (stepId: string): ReactNode =>
      stepId === lineStepId ? <QuietActions {...quiet} /> : null,
    standalone:
      lineStepId === null && hasQuiet(quiet) ? (
        <div className="lcr-agent-draft-line lcr-agent-quiet-line">
          <QuietActions {...quiet} />
        </div>
      ) : null,
  };
}
